import { CalendarIcon, ChevronDown } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react'
import type { DateRange as RdpDateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { useAiVisibilityRuns } from '../hooks/use-ai-visibility-runs'
import { useDataRangeSelection } from './dataset-context'

const RUN_DOT_CLASSNAME =
  'after:pointer-events-none after:absolute after:bottom-1 after:left-1/2 after:z-20 after:size-1 after:-translate-x-1/2 after:rounded-full after:bg-primary [&:has([data-range-start=true])]:after:bg-white [&:has([data-range-end=true])]:after:bg-white [&:has([data-selected-single=true])]:after:bg-white'

function isoToLocalDate(iso: string): Date | null {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return null
  return new Date(year, month - 1, day)
}

function localDateToIso(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function formatIsoAsDisplay(iso: string): string {
  const [year, month, day] = iso.split('-')
  if (!year || !month || !day) return iso
  return `${day}/${month}/${year}`
}

export function RunRangePicker(): ReactElement | null {
  const { t } = useTranslation()
  const { range, setRange } = useDataRangeSelection()
  const runsQuery = useAiVisibilityRuns()
  const runs = useMemo(() => runsQuery.data ?? [], [runsQuery.data])

  const [isOpen, setIsOpen] = useState(false)

  const from = range?.from ?? null
  const to = range?.to ?? null

  const latestRunDate = runs[0]?.exportDate ?? null
  const earliestRunDate = runs[runs.length - 1]?.exportDate ?? null

  const effectiveRange = useMemo<{ from: string; to: string } | null>(() => {
    if (from && to) return { from, to }
    if (latestRunDate) return { from: latestRunDate, to: latestRunDate }
    return null
  }, [from, to, latestRunDate])

  const effectiveRdp = useMemo<RdpDateRange | undefined>(() => {
    if (!effectiveRange) return undefined
    const rangeFrom = isoToLocalDate(effectiveRange.from)
    const rangeTo = isoToLocalDate(effectiveRange.to)
    if (!rangeFrom || !rangeTo) return undefined
    return { from: rangeFrom, to: rangeTo }
  }, [effectiveRange])

  const runDates = useMemo(
    () =>
      runs
        .map((run) => isoToLocalDate(run.exportDate))
        .filter((date): date is Date => date !== null),
    [runs],
  )
  const { minRunDate, maxRunDate } = useMemo(() => {
    if (runDates.length === 0) return { minRunDate: undefined, maxRunDate: undefined }
    const sorted = [...runDates].sort((a, b) => a.getTime() - b.getTime())
    return { minRunDate: sorted[0], maxRunDate: sorted[sorted.length - 1] }
  }, [runDates])

  const [pendingRange, setPendingRange] = useState<RdpDateRange | undefined>(effectiveRdp)

  useEffect(() => {
    if (isOpen) setPendingRange(effectiveRdp)
  }, [isOpen, effectiveRdp])

  const pendingIso = useMemo(() => {
    if (!pendingRange?.from) return null
    return {
      from: localDateToIso(pendingRange.from),
      to: localDateToIso(pendingRange.to ?? pendingRange.from),
    }
  }, [pendingRange])

  const isLatestPreset =
    pendingIso !== null &&
    latestRunDate !== null &&
    pendingIso.from === latestRunDate &&
    pendingIso.to === latestRunDate
  const isAllTimePreset =
    pendingIso !== null &&
    earliestRunDate !== null &&
    latestRunDate !== null &&
    pendingIso.from === earliestRunDate &&
    pendingIso.to === latestRunDate

  const applyPreset = useCallback((fromIso: string, toIso: string) => {
    const fromDate = isoToLocalDate(fromIso)
    const toDate = isoToLocalDate(toIso)
    if (!fromDate || !toDate) return
    setPendingRange({ from: fromDate, to: toDate })
  }, [])

  const triggerLabel = useMemo(() => {
    if (!effectiveRange) return ''
    if (effectiveRange.from === effectiveRange.to) return formatIsoAsDisplay(effectiveRange.from)
    return `${formatIsoAsDisplay(effectiveRange.from)} — ${formatIsoAsDisplay(effectiveRange.to)}`
  }, [effectiveRange])

  const handleCalendarSelect = useCallback(
    (nextRange: RdpDateRange | undefined, selectedDay: Date) => {
      // Clicking the start of an already-complete range clears the selection.
      if (
        pendingRange?.from &&
        pendingRange?.to &&
        localDateToIso(selectedDay) === localDateToIso(pendingRange.from)
      ) {
        setPendingRange(undefined)
        return
      }
      setPendingRange(nextRange)
    },
    [pendingRange],
  )

  const isPendingComplete = Boolean(pendingRange?.from && pendingRange?.to)
  const isPendingCleared = !pendingRange?.from
  const canApply = isPendingComplete || isPendingCleared

  const handleApply = () => {
    if (isPendingComplete && pendingRange?.from && pendingRange?.to) {
      setRange({ from: localDateToIso(pendingRange.from), to: localDateToIso(pendingRange.to) })
    } else {
      // Cleared selection commits as "no range" → default latest run.
      setRange(null)
    }
    setIsOpen(false)
  }

  if (runs.length < 2) return null

  return (
    <div className="flex items-center gap-2">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            aria-label={t('data.runRange.ariaLabel')}
            className="h-auto items-center gap-2 border-neutral-200 bg-white px-3 py-1.5 text-left text-sm font-normal hover:bg-neutral-50"
          >
            <CalendarIcon className="size-4 shrink-0 text-neutral-500" />
            <span className="font-medium text-neutral-700">{t('data.runRange.label')}</span>
            <span className="h-4 w-px bg-neutral-200" aria-hidden="true" />
            <span className="text-neutral-900">{triggerLabel}</span>
            <ChevronDown className="size-4 shrink-0 text-neutral-400" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="flex w-auto max-w-[95vw] flex-col gap-0 p-0 sm:flex-row"
        >
          <div className="flex shrink-0 flex-col gap-1 border-b border-neutral-200 p-2 sm:w-44 sm:border-b-0 sm:border-r">
            <PresetButton
              label={t('data.runRange.presets.latest')}
              active={isLatestPreset}
              disabled={latestRunDate === null}
              onClick={() => latestRunDate && applyPreset(latestRunDate, latestRunDate)}
            />
            <PresetButton
              label={t('data.runRange.presets.allTime')}
              active={isAllTimePreset}
              disabled={earliestRunDate === null || latestRunDate === null}
              onClick={() =>
                earliestRunDate && latestRunDate && applyPreset(earliestRunDate, latestRunDate)
              }
            />
          </div>
          <div className="flex flex-col">
            <div className="p-3">
              <Calendar
                mode="range"
                numberOfMonths={2}
                selected={pendingRange}
                onSelect={handleCalendarSelect}
                defaultMonth={effectiveRdp?.from}
                startMonth={minRunDate}
                endMonth={maxRunDate}
                modifiers={{ hasRun: runDates }}
                modifiersClassNames={{ hasRun: RUN_DOT_CLASSNAME }}
                classNames={{
                  range_start:
                    'rounded-l-md [&>button]:hover:!bg-primary [&>button]:hover:!text-primary-foreground',
                  range_end:
                    'rounded-r-md [&>button]:hover:!bg-primary [&>button]:hover:!text-primary-foreground',
                  range_middle:
                    'rounded-none [&>button]:!bg-neutral-200 [&>button]:!text-neutral-800 [&>button]:hover:!bg-neutral-200',
                }}
                showOutsideDays={false}
                autoFocus
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-neutral-200 p-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-neutral-600 hover:text-neutral-900"
                onClick={() => setIsOpen(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button type="button" size="sm" disabled={!canApply} onClick={handleApply}>
                {t('common.apply')}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}

interface PresetButtonProps {
  label: string
  active: boolean
  disabled: boolean
  onClick: () => void
}

function PresetButton({ label, active, disabled, onClick }: PresetButtonProps): ReactElement {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 text-left text-sm transition-colors',
        active
          ? 'bg-primary/10 font-medium text-primary'
          : 'text-neutral-700 hover:bg-neutral-100 disabled:opacity-40',
      )}
    >
      {label}
    </button>
  )
}
